import type { FormFieldBinding } from '../../../components/formFields';
import FormRow from '../../../components/FormRow';
import FindingDescriptionPicker from '../FindingDescriptionPicker';
import { type FindingRefs } from '../findingRefSets';
import { renderStrataMultiField } from '../strataMultiField';
import type { StrataRouteMap } from '../strataRefRoutes';
import FormSectionBox from '../../../components/FormSectionBox';
import type { DescriptionFormValues } from './descriptionFormValues';

/**
 * How the description relates to the ones around it.
 *
 * TWO KINDS of relation, which is why they look different. The three positional
 * ones (liegt über/unter, gleichzeitig mit) are stored as comma-joined
 * BEFUNDNUMMERN — the ExcaBook wire format — and are picked as chips. "Im
 * Profil mit", "identisch mit" and "Teil von" reference DESCRIPTIONS by id, so
 * they use the description picker and are mirrored onto the other side on save.
 * Both kinds of chip LINK to the record they name; only the way there is
 * resolved differently (see useStrataRouteMap for the number-stored ones).
 *
 * „verfüllt" / „verfüllt durch" belongs to the first kind and is mirrored the
 * same way, but is asked for only on a „baulicher Bestand" — the kind of Befund
 * that has a filling to speak of — and asked LAST, closing the section. The two
 * fields are stored on every description all the same, because the mirror
 * writes the counterpart onto whatever the other side is.
 */
export default function StratigraphySection({
    binding,
    titlePrefix,
    positionalOptions,
    positionalRoutes,
    projectId,
    findingId,
    inProfileWith,
    onInProfileWithChange,
    identical,
    onIdenticalChange,
    partOf,
    onPartOfChange,
    onOpenRef,
    showFilling = false
}: {
    binding: FormFieldBinding<DescriptionFormValues>;
    titlePrefix: string;
    /** Sibling descriptions, formatted "Bef. 7000, Fl. 1, Pl. 1". */
    positionalOptions: readonly string[];
    /** Where each picked Befundnummer's chip links to (useStrataRouteMap). */
    positionalRoutes: StrataRouteMap;
    projectId: string;
    /** The Befund this description belongs to — excluded from its own picker. */
    findingId: string;
    inProfileWith: FindingRefs;
    onInProfileWithChange: (refs: FindingRefs) => void;
    identical: FindingRefs;
    onIdenticalChange: (refs: FindingRefs) => void;
    partOf: FindingRefs;
    onPartOfChange: (refs: FindingRefs) => void;
    onOpenRef: (path: string) => void;
    /** Show „verfüllt" / „verfüllt durch" — a baulicher Bestand only. */
    showFilling?: boolean;
}) {
    const strata = (
        name:
            | 'overliesOrCuts'
            | 'underliesOrIsCut'
            | 'contemporaryWith'
            | 'fills'
            | 'filledBy'
    ) =>
        renderStrataMultiField(binding, name, positionalOptions, {
            routes: positionalRoutes,
            onOpen: onOpenRef
        });
    const picker = (
        field: 'inProfileWith' | 'identicalWith' | 'partOf',
        value: FindingRefs,
        onChange: (refs: FindingRefs) => void
    ) => (
        <FindingDescriptionPicker
            projectId={projectId}
            value={value}
            onChange={onChange}
            label={binding.intl.formatMessage({
                id: binding.labelId(field)
            })}
            excludeFindingId={findingId}
            descriptionsOnly
            onOpenRef={onOpenRef}
        />
    );
    return (
        <FormSectionBox titlePrefix={titlePrefix} id="stratigraphy">
            <FormRow>
                {strata('overliesOrCuts')}
                {strata('underliesOrIsCut')}
            </FormRow>
            <FormRow>{strata('contemporaryWith')}</FormRow>
            {picker('inProfileWith', inProfileWith, onInProfileWithChange)}
            {picker('identicalWith', identical, onIdenticalChange)}
            {picker('partOf', partOf, onPartOfChange)}
            {/* Last of the section, after the identity relations — and in the
                same order the sheet prints them (see DescriptionBody). */}
            {showFilling && (
                <FormRow>
                    {strata('fills')}
                    {strata('filledBy')}
                </FormRow>
            )}
        </FormSectionBox>
    );
}
